import { OrderListFilterProviderType, OrderListFilterContextProvider, OrderListFilter, OrderListView } from './context';
import { ReactNode, useMemo, useState } from 'react';
import { paginationComponent, viewComponent } from './utils';

type Props = {
  children: ReactNode;
} & Partial<OrderListFilter>;

export const OrderListStaticFilterProvider = ({
  children,
  page,
  pageSize,
  customerIds,
  salesRepIds,
  showroomIds,
  orderStatuses,
  dateFrom,
  dateTo,
  view,
}: Props) => {
  /**
   * Which orders are listed is fixed by the embedding page, but how much of each order is
   * shown is the viewer's choice, so the view is the one field held in local state. These
   * panels have no URL of their own to persist it to, unlike the Orders page.
   *
   * Only the viewer's own pick is stored; until they make one the `view` prop decides. It
   * carries the tenant default, which arrives with the session and so can still be absent
   * on first render — seeding state from it once would pin the panel to whatever was
   * showing while the session loaded.
   */
  const [pickedView, setPickedView] = useState<OrderListView | null>(null);
  const currentView = pickedView ?? viewComponent({ view }).view;

  const filterProps = { page, pageSize, customerIds, salesRepIds, showroomIds, orderStatuses, dateFrom, dateTo };
  const filter = useMemo<OrderListFilter>(
    () => ({ ...filterProps, ...paginationComponent(filterProps), view: currentView }),
    [page, pageSize, customerIds, salesRepIds, showroomIds, orderStatuses, dateFrom, dateTo, currentView]
  );

  const contextValue = useMemo<OrderListFilterProviderType>(
    () => ({
      filter,
      updateFilter: (newFilter) => {
        const { view: nextView, ...rest } = newFilter;
        if (Object.keys(rest).length > 0) {
          throw new Error('Filter is static and cannot be updated');
        }
        if (nextView) setPickedView(nextView);
      },
    }),
    [filter]
  );

  return <OrderListFilterContextProvider value={contextValue}>{children}</OrderListFilterContextProvider>;
};
